import { Controller } from "@hotwired/stimulus"

// Resignation-letter builder modal. Portals to <body>, opens via
// [data-resignation-builder-open] markers (edit adds data-resignation-id).
// Direct-field input + tone chips; live preview interpolates manager/company/
// last-working-day + the embedded full name; Generate (premium-gated, 403 →
// upgrade) / Regenerate produce versions; Save PATCHes; Print opens a print window.
export default class extends Controller {
  static targets = ["company", "manager", "lastDay", "role", "noticePeriod", "reason", "tones",
                    "preview", "previewBody", "previewName", "editText", "versionBar", "versionLabel",
                    "generateBtn", "generateLabel", "saveBtn", "error", "step"]
  static values = { generateUrl: String, showUrlTemplate: String, fullName: String }

  connect() {
    if (this.element.parentElement !== document.body) document.body.appendChild(this.element)
    this._open = (e) => this.open(e)
    document.querySelectorAll("[data-resignation-builder-open]").forEach((b) => b.addEventListener("click", this._open))
    this._onKey = (e) => { if (e.key === "Escape") this.close() }
    this.versions = []; this.activeIndex = 0; this.resignationId = ""; this.tone = "professional"
    const p = new URLSearchParams(window.location.search)
    if (p.get("new") === "1") this.open()
  }

  disconnect() {
    document.querySelectorAll("[data-resignation-builder-open]").forEach((b) => b.removeEventListener("click", this._open))
  }

  open(event) {
    const id = event && event.currentTarget && event.currentTarget.getAttribute("data-resignation-id")
    this.element.classList.remove("hidden"); this.element.classList.add("flex")
    document.addEventListener("keydown", this._onKey)
    this._resetState()
    if (this.hasPreviewNameTarget) this.previewNameTarget.textContent = this.fullNameValue || "Your Name"
    if (id) this._prefill(id)
    this._renderPreview()
  }

  close() {
    this.element.classList.add("hidden"); this.element.classList.remove("flex")
    document.removeEventListener("keydown", this._onKey)
  }
  backdrop(e) { if (e.target === this.element) this.close() }
  stop(e) { e.stopPropagation() }

  selectTone(e) {
    this.tone = e.currentTarget.getAttribute("data-tone") || "professional"
    this._syncToneChips()
  }

  inputChanged() { this._renderPreview() }

  _renderPreview() {
    if (this.versions.length) return this._renderVersion()
    if (!this.hasPreviewBodyTarget) return
    const company = this._val(this.companyTarget) || "the company"
    const manager = this._val(this.managerTarget) || "Hiring Manager"
    const lastDay = this._val(this.lastDayTarget) || "[last working day]"
    const hl = (t) => `<span class="bg-primary-container/30 px-1 rounded">${this._esc(t)}</span>`
    this.previewBodyTarget.innerHTML = `
      <p class="text-right text-text-muted mb-6">[Current Date]</p>
      <p>Dear ${hl(manager)},</p>
      <p>Please accept this letter as formal notification that I am resigning from my position at ${hl(company)}. My last day of employment will be ${hl(lastDay)}.</p>
      <p>Thank you for the opportunities I've had during my time here. I am committed to ensuring a smooth transition of my responsibilities before my departure.</p>
      <p>Sincerely,<br>${this._esc(this.fullNameValue || "")}</p>`
  }

  _renderVersion() {
    const text = this.versions[this.activeIndex] || ""
    if (this.hasPreviewBodyTarget) {
      this.previewBodyTarget.innerHTML = text.split(/\n{2,}/).map((p) => `<p>${this._esc(p).replace(/\n/g, "<br>")}</p>`).join("")
    }
    if (this.hasEditTextTarget) this.editTextTarget.value = text
    if (this.hasVersionBarTarget) this.versionBarTarget.classList.toggle("hidden", this.versions.length < 2)
    if (this.hasVersionLabelTarget) this.versionLabelTarget.textContent = `Version ${this.activeIndex + 1} / ${this.versions.length}`
    if (this.hasSaveBtnTarget) this.saveBtnTarget.disabled = false
    this._setStep(2)
  }

  prevVersion() { if (this.activeIndex > 0) { this.activeIndex--; this._renderVersion() } }
  nextVersion() { if (this.activeIndex < this.versions.length - 1) { this.activeIndex++; this._renderVersion() } }

  toggleEdit() {
    if (!this.hasEditTextTarget || !this.hasPreviewBodyTarget) return
    const editing = this.editTextTarget.classList.toggle("hidden") === false
    this.previewBodyTarget.classList.toggle("hidden", editing)
  }
  editChanged() { if (this.versions.length) this.versions[this.activeIndex] = this.editTextTarget.value }

  generate() { this._generate(false) }
  regenerate() { this._generate(true) }

  async _generate(variation) {
    this._clearError(); this._busy(true)
    const fd = new FormData()
    fd.append("company_name", this._val(this.companyTarget))
    fd.append("manager_name", this._val(this.managerTarget))
    fd.append("last_working_day", this._val(this.lastDayTarget))
    fd.append("role", this._val(this.roleTarget))
    fd.append("notice_period", this._val(this.noticePeriodTarget))
    fd.append("reason", this._val(this.reasonTarget))
    fd.append("tone", this.tone)
    fd.append("full_name", this.fullNameValue || "")
    if (this.resignationId) fd.append("resignation_id", this.resignationId)
    if (variation) fd.append("variation", "true")
    try {
      const res = await fetch(this.generateUrlValue, {
        method: "POST", credentials: "same-origin",
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrf() }, body: fd
      })
      if (res.status === 403) { window.location = "/users/premium_subscribe_direct"; return }
      const data = await res.json().catch(() => ({}))
      if (data && data.success && data.body) {
        this.versions.push(data.body); this.activeIndex = this.versions.length - 1
        if (data.public_id) this.resignationId = data.public_id
        this._renderVersion()
      } else {
        this._error((data && data.error) || "Generation failed. Please try again.")
      }
    } catch (e) { this._error("Network error. Please try again.") }
    finally { this._busy(false) }
  }

  async save() {
    if (!this.resignationId || !this.versions.length) return this.close()
    const url = this.showUrlTemplateValue.replace(":id", this.resignationId)
    const fd = new FormData()
    fd.append("resignation_letter[body]", this.versions[this.activeIndex])
    fd.append("resignation_letter[company_name]", this._val(this.companyTarget))
    fd.append("resignation_letter[manager_name]", this._val(this.managerTarget))
    fd.append("resignation_letter[role]", this._val(this.roleTarget))
    fd.append("resignation_letter[tone]", this.tone)
    // Persist the remaining form targets — controller permits these and
    // _prefill reads them back, but they used to be silently dropped on
    // Save Draft so a user edit to Last Working Day / Notice Period /
    // Reason would not survive a reload.
    if (this.hasLastDayTarget)       fd.append("resignation_letter[last_working_day]", this._val(this.lastDayTarget))
    if (this.hasNoticePeriodTarget)  fd.append("resignation_letter[notice_period]",    this._val(this.noticePeriodTarget))
    if (this.hasReasonTarget)        fd.append("resignation_letter[reason]",           this._val(this.reasonTarget))
    fd.append("_method", "patch")
    try {
      await fetch(url, { method: "POST", credentials: "same-origin",
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrf() }, body: fd })
    } catch (e) { /* non-fatal */ }
    this.close(); window.location.reload()
  }

  print() {
    if (!this.versions.length) return
    const name = this.fullNameValue || ""
    const body = this.versions[this.activeIndex]
    const w = window.open("", "_blank", "width=800,height=900")
    if (!w) return
    const esc = (s) => { const d = w.document.createElement("div"); d.textContent = s == null ? "" : String(s); return d.innerHTML }
    const paras = body.split(/\n{2,}/).map((p) => `<p>${esc(p).replace(/\n/g, "<br>")}</p>`).join("")
    w.document.write(`<!doctype html><html><head><title>Resignation Letter</title><style>body{font-family:'Hanken Grotesk',Arial,sans-serif;max-width:680px;margin:48px auto;padding:0 24px;color:#1b1c18;line-height:1.6}h1{font-size:24px;margin-bottom:24px}p{margin:0 0 14px}</style></head><body><h1>${esc(name)}</h1>${paras}</body></html>`)
    w.document.close(); w.focus(); w.print()
    this._setStep(3)
  }

  async _prefill(id) {
    try {
      const res = await fetch(this.showUrlTemplateValue.replace(":id", id),
        { credentials: "same-origin", headers: { "Accept": "application/json" } })
      if (!res.ok) return
      const d = await res.json()
      this._set(this.companyTarget, d.company_name); this._set(this.managerTarget, d.manager_name)
      this._set(this.lastDayTarget, d.last_working_day); this._set(this.roleTarget, d.role)
      this._set(this.noticePeriodTarget, d.notice_period); this._set(this.reasonTarget, d.reason)
      if (d.tone) { this.tone = d.tone; this._syncToneChips() }
      if (d.body) { this.versions = [d.body]; this.activeIndex = 0; this.resignationId = d.public_id; this._renderVersion() }
    } catch (e) { /* show fresh */ }
  }

  _syncToneChips() {
    if (!this.hasTonesTarget) return
    this.tonesTarget.querySelectorAll("[data-tone]").forEach((b) => {
      const on = b.getAttribute("data-tone") === this.tone
      b.classList.toggle("bg-primary-container", on)
      b.classList.toggle("text-on-primary-container", on)
      b.classList.toggle("bg-surface-card", !on)
      b.classList.toggle("text-on-surface-variant", !on)
      b.classList.toggle("border", !on)
      b.classList.toggle("border-border-light", !on)
    })
  }

  _setStep(n) {
    if (!this.hasStepTarget) return
    this.stepTargets.forEach((el) => {
      const i = Number(el.dataset.step || 0)
      const active = i <= n
      el.classList.toggle("bg-primary-container", active); el.classList.toggle("text-on-primary", active)
      el.classList.toggle("bg-surface-card", !active); el.classList.toggle("text-on-surface-variant", !active)
    })
  }

  _resetState() {
    this.versions = []; this.activeIndex = 0; this.resignationId = ""; this.tone = "professional"
    if (this.hasVersionBarTarget) this.versionBarTarget.classList.add("hidden")
    if (this.hasSaveBtnTarget) this.saveBtnTarget.disabled = true
    if (this.hasEditTextTarget) this.editTextTarget.classList.add("hidden")
    if (this.hasPreviewBodyTarget) this.previewBodyTarget.classList.remove("hidden")
    this._syncToneChips(); this._setStep(1); this._clearError()
  }
  _val(t) { return t ? (t.value || "").trim() : "" }
  _set(t, v) { if (t) t.value = v || "" }
  _busy(on) {
    if (this.hasGenerateBtnTarget) this.generateBtnTarget.disabled = on
    if (this.hasGenerateLabelTarget) this.generateLabelTarget.textContent = on ? "Generating…" : "Generate with AI"
  }
  _csrf() { return document.querySelector('meta[name="csrf-token"]')?.content || "" }
  _esc(s) { const d = document.createElement("div"); d.textContent = s == null ? "" : String(s); return d.innerHTML }
  _error(m) { if (this.hasErrorTarget) { this.errorTarget.textContent = m; this.errorTarget.classList.remove("hidden") } }
  _clearError() { if (this.hasErrorTarget) this.errorTarget.classList.add("hidden") }
}
